import React, { useEffect, useState } from "react";
import { usePermissions } from "../../PermissionsProvider";
import { PermissionsService } from "../../services/PermissionsService";
import { PlusIcon } from "lucide-react";
import { Key } from "../../model_gen/key";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { ProviderKeyMap } from "../../types/IDevelopers";
import KeysTable from "../shared/KeysTable";

const DevelopersAPIKeys: React.FC = () => {
  const defaultKeyMap = {
    openai_api_key: 0,
    groq_api_key: 0,
    anthropic_api_key: 0,
    cerebras_api_key: 0,
    azure_api_key: 0,
    sambanova_api_key: 0,
    gemini_api_key: 0,
    deepseek_api_key: 0,
  };
  
  const defaultKey: Partial<Key> = {
    key_map: defaultKeyMap,
    owner: "USER",
    type: "Maitai",
    description: "",
  };

  const { apiKey } = usePermissions();
  const [keys, setKeys] = useState<any[]>([]);
  const [visibleKey, setVisibleKey] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentKey, setCurrentKey] = useState<Key>(defaultKey);
  const [editKey, setEditKey] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<Key | null>(null);

  const keyTypeOptions = [
    { label: "Maitai", value: "Maitai" },
    { label: "OpenAI", value: "OpenAI" },
    { label: "Groq", value: "Groq" },
    { label: "Anthropic", value: "Anthropic" },
    { label: "Cerebras", value: "Cerebras" },
    { label: "Sambanova", value: "Sambanova" },
    { label: "Gemini", value: "Gemini" },
    { label: "Deepseek", value: "Deepseek" },
  ];

  useEffect(() => {
    refreshKeys();
  }, []);

  const refreshKeys = () => {
    setIsLoading(true);
    PermissionsService.getKeys(setIsLoading, apiKey).subscribe(
      (data: Key[]) => {
        setKeys(data);
      },
      (error: any) => {
        console.error("Error fetching keys:", error);
        setIsLoading(false);
      }
    );
  };

  const toggleVisibility = (key: string) => {
    setVisibleKey(visibleKey === key ? null : key);
  };

  const showModal = (key?: Key) => {
    setCurrentKey(key || defaultKey);
    setEditKey(!!key);
    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsSubmitting(true);
    if (editKey) {
      PermissionsService.editKey(currentKey, null, apiKey)
        .subscribe(
          (response: Key) => {
            refreshKeys();
            setIsModalVisible(false);
          },
          (error: any) => {
            console.error("Error editing key:", error);
          }
        )
        .add(() => setIsSubmitting(false));
    } else {
      if (!currentKey) return;
      PermissionsService.addKey(currentKey, null, apiKey)
        .subscribe(
          (response: Key) => {
            refreshKeys();
            setIsModalVisible(false);
          },
          (error: any) => {
            console.error("Error adding key:", error);
          }
        )
        .add(() => setIsSubmitting(false));
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const copyKeyValue = (keyValue: string) => {
    navigator.clipboard.writeText(keyValue);
    // You could add a toast notification here if available
  };

  const handleDeleteKey = (key: Key) => {
    setKeyToDelete(key);
    setShowDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!keyToDelete) return;
    
    PermissionsService.deleteKey(keyToDelete, null, apiKey).subscribe(
      (response: any) => {
        console.log("Key removed successfully:", response);
        refreshKeys();
        setShowDeleteDialog(false);
        setKeyToDelete(null);
      },
      (error: any) => {
        console.error("Error removing key:", error);
        setShowDeleteDialog(false);
        setKeyToDelete(null);
      }
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">API Keys</h3>
          <p className="text-sm text-muted-foreground">Manage your API keys and provider configurations</p>
        </div>
        <Button
          onClick={() => showModal()}
          className="gap-2"
        >
          <PlusIcon className="w-4 h-4" />
          New API Key
        </Button>
      </div>

      <KeysTable
        keys={keys}
        loading={isLoading}
        visibleKey={visibleKey}
        onToggleVisibility={toggleVisibility}
        onCopyKey={copyKeyValue}
        onEditKey={(key) => showModal(key)}
        onDeleteKey={handleDeleteKey}
      />

      {/* Add/Edit Key Modal */}
      <Dialog open={isModalVisible} onOpenChange={setIsModalVisible}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editKey ? "Edit API Key" : "Add API Key"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="keyType">Key Type</Label>
              <Select
                value={currentKey?.type}
                onValueChange={(value) =>
                  setCurrentKey(currentKey ? { ...currentKey, type: value } : currentKey)
                }
                disabled={editKey}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select key type" />
                </SelectTrigger>
                <SelectContent>
                  {keyTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                placeholder="Enter a description for this API key"
                value={currentKey?.description || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCurrentKey(
                    currentKey
                      ? { ...currentKey, description: e.target.value }
                      : currentKey
                  )
                }
                disabled={isSubmitting}
              />
            </div>

            {currentKey?.type && currentKey.type !== "Maitai" && (
              <div className="space-y-2">
                <Label htmlFor="keyValue">Key Value</Label>
                <Input
                  id="keyValue"
                  type="password"
                  placeholder="Enter the API key value"
                  value={currentKey?.key_value || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setCurrentKey(
                      currentKey
                        ? { ...currentKey, key_value: e.target.value }
                        : currentKey
                    )
                  }
                  disabled={isSubmitting}
                />
              </div>
            )}

            {currentKey?.type === "Maitai" && (
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Maitai keys are composite keys that manage multiple provider keys. 
                  The individual provider configurations will be available after creation.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleOk} disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : editKey ? "Update Key" : "Create Key"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this API key? This action cannot be undone.
            </p>
            {keyToDelete && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-gc-med">{keyToDelete.type}</p>
                <p className="text-xs text-muted-foreground">{keyToDelete.description}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DevelopersAPIKeys;
